import React, {
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from 'react';
import {
  ChatContainer,
  MessagesContainer,
  MessageBubble,
  MessageContent,
  MessageSender,
  MessageTime,
} from './Ask.styles';
import { AskMessage, AskUser, AskSessionCitation } from './Ask';
import { MarkdownRenderer } from './MarkdownRenderer';
import { TypingIndicator } from './TypingIndicator';
import { useToast } from '@/components/common/Toast/ToastContainer';
import { formatLastUsed } from '@/utils/data/transformWorkSpaceItems';
import styled from 'styled-components';
import { Spacer } from '@tw/tw-component-library';

const DateSeparator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 1rem 0;
  position: relative;
  width: 100%;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: 1px;
    background-color: #e5e7eb;
  }

  span {
    background-color: ${({ theme }) => theme.colors?.background || '#f9fafb'};
    padding: 0.25rem 0.75rem;
    font-size: 0.75rem;
    color: #6b7280;
    position: relative;
    z-index: 1;
  }
`;

const MessageGroup = styled.div<{ $showSender: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: ${({ $showSender }) => ($showSender ? '0.75rem' : '0.25rem')};
`;

const RetryButton = styled.button`
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  cursor: pointer;
  margin-top: 0.25rem;
  transition: background 0.2s;

  &:hover {
    background: #dc2626;
  }
`;

const EmptyMessageContent = styled(MessageContent)`
  text-align: center;
  padding: 2rem;
`;

const HighlightedContent = styled.div`
  word-break: break-word;
`;

const TextWithLinks = styled.span`
  word-break: break-word;
`;

const MessageBubbleWrapper = styled(MessageBubble)<{
  $isSelected: boolean;
  $isHighlighted: boolean;
}>`
  position: relative;
  outline: ${({ $isSelected, $isHighlighted }) =>
    $isSelected
      ? '2px solid #3b82f6'
      : $isHighlighted
        ? '2px solid #fbbf24'
        : 'none'};
  border-radius: ${({ $isSelected, $isHighlighted }) =>
    $isSelected || $isHighlighted ? '4px' : '0'};
  transition: background-color 0.3s ease;
`;

const FailedMessageContent = styled(MessageContent)`
  opacity: 0.6;
  border: 1px solid #ef4444;
`;

const MessageActionsContainer = styled.div<{ $isOwn: boolean }>`
  position: absolute;
  top: -1.5rem;
  right: ${({ $isOwn }) => ($isOwn ? '0.5rem' : 'auto')};
  left: ${({ $isOwn }) => ($isOwn ? 'auto' : '33rem')};
  display: flex;
  gap: 0.25rem;
  opacity: 0;
  transition: opacity 0.2s;
`;

const MessageActionButton = styled.button<{
  $isOwn: boolean;
  $isPinned?: boolean;
}>`
  background: rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  font-size: 0.75rem;
  color: ${({ $isOwn, $isPinned }) =>
    $isPinned ? '#fbbf24' : $isOwn ? '#1f2937' : '#ffffff'};
  z-index: 10;
  transition: color 0.2s;
`;

const MessageTimeWrapper = styled(MessageTime)`
  cursor: help;
`;

const TokenUsageSpan = styled.span`
  margin-left: 0.5rem;
  opacity: 0.7;
`;

const SelectionBar = styled.div`
  padding: 0.5rem 1rem;
  background: #3b82f6;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
`;

const SelectionActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const SelectionButton = styled.button`
  padding: 0.25rem 0.5rem;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  color: white;
  cursor: pointer;
`;

const ScrollButtonContainer = styled.div`
  position: sticky;
  bottom: 1rem;
  display: flex;
  justify-content: center;
  margin-top: 0.5rem;
`;

const ScrollButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  transition: all 0.2s;

  &:hover {
    background: #2563eb;
    transform: scale(1.1);
  }
`;

const CitationsSection = styled.div``;

const CitationsAccordionHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.35rem 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;

  &:hover {
    color: #4b5563;
  }
`;

const CitationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const CitationItem = styled.div<{ $color: string; $clickable?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  padding: 0.35rem 0.5rem;
  margin-bottom: 0.35rem;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
  border-left: 3px solid ${({ $color }) => $color};
  scroll-margin-top: 1rem;
  scroll-margin-bottom: 1rem;
  transition:
    background-color 0.4s ease-out,
    box-shadow 0.2s ease-out;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background: ${({ $clickable }) =>
      $clickable ? 'rgba(0, 0, 0, 0.06)' : 'rgba(0, 0, 0, 0.04)'};
  }

  &:target {
    background-color: ${({ $color }) => `${$color}20`};
    box-shadow: 0 0 0 2px ${({ $color }) => $color};
  }
`;

const CitationNumber = styled.span<{ $color: string }>`
  font-size: 0.6875rem;
  font-weight: 700;
  color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const CitationSnippet = styled.div`
  font-size: 0.75rem;
  line-height: 1.35;
  color: #4b5563;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const AssistantContentWrapper = styled.div`
  & .citation-ref {
    text-decoration: underline;
    cursor: pointer;
    color: #3b82f6;
  }
`;

interface ChatMessagesProps {
  messages: AskMessage[];
  currentUserId: string;
  users: AskUser[];
  isTyping?: boolean;
  typingUserName?: string;
  responseStartTime?: number; // Timestamp when response started
  /** Optional status from SignalR (e.g. "Determining audience in Ask."); shown in typing indicator. */
  processingStatus?: string | null;
  onRetryMessage?: (messageId: string) => void;
  failedMessageIds?: Set<string>;
  searchQuery?: string;
  highlightedMessageId?: string;
  onMessageClick?: (messageId: string) => void;
  pinnedMessageIds?: Set<string>;
  onPinMessage?: (messageId: string) => void;
  /** Called when user clicks a citation (open PDF at page). Pass messageId so the citation document can be fetched. */
  onCitationClick?: (citation: AskSessionCitation, messageId: string) => void;
  /** Incrementing trigger value from parent to request scroll-to-bottom. */
  scrollToBottomTrigger?: number;
}

/** Placeholder format: hyphens only (underscores are eaten by markdown italic _x_) */
const CITATION_PLACEHOLDER_PREFIX = 'CITATION-';
const ENABLE_SELECTED_TEXT_COPY = false;

/** Replaces citation refs [n] with placeholders for MarkdownRenderer to turn into links */
const injectCitationPlaceholders = (
  content: string,
  messageId: string,
): string => {
  if (!content || !messageId) return content;
  return content.replace(
    /\[(\d+)\]/g,
    (_, ref) => `${CITATION_PLACEHOLDER_PREFIX}${ref}-${messageId}`,
  );
};

/** Palette for colour-coding citations (same order as in MarkdownRenderer) */
export const CITATION_COLORS = [
  '#2563eb', // blue
  '#059669', // emerald
  '#b45309', // amber
  '#dc2626', // red
  '#7c3aed', // violet
  '#db2777', // pink
  '#0891b2', // cyan
  '#65a30d', // lime
] as const;

/** Returns a consistent colour for a citation ref number */
export const getCitationColor = (citationRef: string): string => {
  const index = parseInt(citationRef, 10) % CITATION_COLORS.length;
  return CITATION_COLORS[Number.isNaN(index) ? 0 : index];
};

/** Injects HTML anchor tags for citation refs [n] (for use with dangerouslySetInnerHTML when search is active) */
const injectCitationLinksHtml = (
  content: string,
  getColor: (ref: string) => string,
): string => {
  if (!content) return content;
  return content.replace(/\[(\d+)\]/g, (_, ref) => {
    const color = getColor(ref);
    return `<a href="#" class="citation-ref" data-citation-ref="${ref}" role="button" style="color:${color};font-weight:600;text-decoration:underline;">[${ref}]</a>`;
  });
};

// Utility function to make URLs clickable
const makeLinksClickable = (text: string): string => {
  if (!text) return text;

  // URL regex pattern
  const urlPattern = /(https?:\/\/[^\s]+)/g;
  return text.replace(
    urlPattern,
    '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: inherit; text-decoration: underline;">$1</a>',
  );
};

// Format relative time using the shared formatLastUsed utility
const formatRelativeTime = (date: Date): string => {
  return formatLastUsed(date, { compact: true });
};

// Get date label for grouping
const getDateLabel = (date: Date): string => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const messageDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  const diffDays = Math.floor(
    (today.getTime() - messageDate.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return 'This week';
  if (diffDays < 30) return 'This month';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

// Highlight search terms in text
const highlightSearchTerm = (text: string, searchQuery?: string): string => {
  if (!searchQuery || !text) return text;
  const regex = new RegExp(
    `(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
    'gi',
  );
  return text.replace(
    regex,
    '<mark style="background-color: #fef08a; padding: 0.125rem 0.25rem; border-radius: 2px;">$1</mark>',
  );
};

export const ChatMessages: React.FC<ChatMessagesProps> = ({
  messages,
  currentUserId,
  users,
  isTyping = false,
  typingUserName = 'Assistant',
  responseStartTime,
  processingStatus = null,
  onRetryMessage,
  failedMessageIds = new Set(),
  searchQuery,
  highlightedMessageId,
  onMessageClick,
  pinnedMessageIds = new Set(),
  onPinMessage,
  onCitationClick,
  scrollToBottomTrigger,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [newMessageIds, setNewMessageIds] = useState<Set<string>>(new Set());
  const previousMessageIdsRef = useRef<Set<string>>(new Set());
  const [selectedMessageIds, setSelectedMessageIds] = useState<Set<string>>(
    new Set(),
  );
  const [selectedText, setSelectedText] = useState<string>('');
  const [expandedCitationMessageIds, setExpandedCitationMessageIds] = useState<
    Set<string>
  >(new Set());
  const { showSuccess } = useToast();

  // When user clicks a citation link in content, open the PDF modal (same as clicking in Citations used)
  const handleCitationLinkClick = useCallback(
    (message: AskMessage) => (e: React.MouseEvent) => {
      const link = (e.target as HTMLElement).closest?.('.citation-ref');
      if (!link || !message.citations?.length || !onCitationClick) return;
      e.preventDefault();
      const ref = link.getAttribute('data-citation-ref');
      const citation = message.citations.find((c) => c.citationRef === ref);
      if (citation) onCitationClick(citation, message.id);
    },
    [onCitationClick]
  );

  // Check if user is near bottom of scroll container
  const checkScrollPosition = useCallback(() => {
    if (!messagesContainerRef.current) return;

    const container = messagesContainerRef.current;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const isNearBottom = distanceFromBottom < 100; // 100px threshold

    setShouldAutoScroll(isNearBottom);
    setShowScrollButton(!isNearBottom && messages.length > 0);
  }, [messages.length]);

  // Track new messages for animation - compare by IDs to avoid false triggers
  useEffect(() => {
    const currentMessageIds = new Set(messages.map((msg) => msg.id));
    const previousMessageIds = previousMessageIdsRef.current;

    // Find truly new messages (in current but not in previous)
    const newMessages = messages.filter(
      (msg) => !previousMessageIds.has(msg.id),
    );

    if (newMessages.length > 0) {
      newMessages.forEach((message) => {
        setNewMessageIds((prev) => new Set([...prev, message.id]));

        // Remove the "new" flag after animation completes
        setTimeout(() => {
          setNewMessageIds((prev) => {
            const next = new Set(prev);
            next.delete(message.id);
            return next;
          });
        }, 300); // Match animation duration (0.3s)
      });
    }

    // Update previous message IDs
    previousMessageIdsRef.current = currentMessageIds;
  }, [messages]);

  // Auto-scroll when user is near bottom—including when streaming message content grows (messages reference changes).
  const messagesScrollKey = messages.map((m) => `${m.id}:${m.content.length}`).join('|');
  useEffect(() => {
    if (shouldAutoScroll && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messagesScrollKey, shouldAutoScroll]);

  // Check scroll position on scroll
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', checkScrollPosition);
    checkScrollPosition(); // Initial check

    return () => {
      container.removeEventListener('scroll', checkScrollPosition);
    };
  }, [checkScrollPosition]);

  // Track elapsed time for long responses
  useEffect(() => {
    if (!responseStartTime) {
      setElapsedTime(0);
      return;
    }

    const interval = setInterval(() => {
      const elapsed = (Date.now() - responseStartTime) / 1000;
      setElapsedTime(elapsed);
    }, 100); // Update every 100ms for smooth progress

    return () => clearInterval(interval);
  }, [responseStartTime]);

  // Memoize users map for quick lookups
  const usersMap = useMemo(() => {
    return new Map(users.map((user) => [user.id, user]));
  }, [users]);

  const getSenderColor = useCallback(
    (senderId: string): string => {
      const user = usersMap.get(senderId);
      if (user?.color) return user.color;

      // Generate a consistent color based on senderId
      const colors = [
        '#3b82f6', // blue
        '#10b981', // green
        '#f59e0b', // amber
        '#ef4444', // red
        '#8b5cf6', // purple
        '#ec4899', // pink
        '#06b6d4', // cyan
        '#f97316', // orange
      ];
      const index = senderId
        .split('')
        .reduce((acc, char) => acc + char.charCodeAt(0), 0);
      return colors[index % colors.length];
    },
    [usersMap],
  );

  const formatRelativeTimeMemo = useCallback((date: Date): string => {
    return formatRelativeTime(date);
  }, []);

  const formatFullTime = useCallback((date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  }, []);

  const handleCopyMessage = useCallback(
    async (content: string) => {
      try {
        await navigator.clipboard.writeText(content);
        showSuccess('Copied to clipboard');
      } catch (err) {
        console.error('Failed to copy message:', err);
      }
    },
    [showSuccess],
  );

  // Handle text selection
  useEffect(() => {
    if (!ENABLE_SELECTED_TEXT_COPY) {
      setSelectedText('');
      return;
    }

    const handleSelection = () => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        setSelectedText('');
        return;
      }

      const selectedTextContent = selection.toString().trim();
      if (selectedTextContent) {
        setSelectedText(selectedTextContent);
      } else {
        setSelectedText('');
      }
    };

    document.addEventListener('selectionchange', handleSelection);
    return () => {
      document.removeEventListener('selectionchange', handleSelection);
    };
  }, []);

  // Copy selected text
  const handleCopySelectedText = useCallback(async () => {
    if (!selectedText) return;
    try {
      await navigator.clipboard.writeText(selectedText);
      showSuccess(`Copied ${selectedText.length} characters`);
      window.getSelection()?.removeAllRanges();
      setSelectedText('');
    } catch (err) {
      console.error('Failed to copy selected text:', err);
    }
  }, [selectedText, showSuccess]);

  // Toggle message selection
  const toggleMessageSelection = useCallback((messageId: string) => {
    setSelectedMessageIds((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  }, []);

  // Copy selected messages
  const handleCopySelectedMessages = useCallback(async () => {
    if (selectedMessageIds.size === 0) return;
    const selectedMessages = messages.filter((msg) =>
      selectedMessageIds.has(msg.id),
    );
    const textToCopy = selectedMessages
      .map(
        (msg) =>
          `${msg.senderName} (${formatRelativeTimeMemo(msg.timestamp)}): ${
            msg.content
          }`,
      )
      .join('\n\n');
    try {
      await navigator.clipboard.writeText(textToCopy);
      showSuccess(`Copied ${selectedMessageIds.size} message(s)`);
      setSelectedMessageIds(new Set());
    } catch (err) {
      console.error('Failed to copy selected messages:', err);
    }
  }, [selectedMessageIds, messages, formatRelativeTimeMemo, showSuccess]);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      setShouldAutoScroll(true);
      setShowScrollButton(false);
    }
  }, []);

  // When parent requests scroll (e.g. after user sends a message), scroll to bottom.
  useEffect(() => {
    if (scrollToBottomTrigger != null && scrollToBottomTrigger > 0) {
      scrollToBottom();
    }
  }, [scrollToBottomTrigger, scrollToBottom]);

  const isCurrentUser = useCallback(
    (senderId: string): boolean => {
      return senderId === currentUserId;
    },
    [currentUserId],
  );

  // Calculate total token usage for Assistant messages
  const calculateTokenUsage = useCallback(
    (tokenUsage?: AskMessage['tokenUsage']): number | null => {
      if (!tokenUsage) return null;

      // Sum all token values
      const values = Object.values(tokenUsage);
      if (values.length === 0) return null;

      const total = values.reduce((sum: number, value) => {
        return sum + (typeof value === 'number' ? value : 0);
      }, 0);

      return total > 0 ? total : null;
    },
    [],
  );

  // Group messages by date and sender
  const groupedMessages = useMemo(() => {
    if (messages.length === 0) return [];

    const groups: Array<{
      dateLabel?: string;
      messages: Array<{
        message: AskMessage;
        showSender: boolean;
        showDate: boolean;
      }>;
    }> = [];

    let currentDateLabel: string | undefined;
    let lastSenderId: string | undefined;
    let lastDate: Date | undefined;

    messages.forEach((message, index) => {
      const messageDate = new Date(message.timestamp);
      const dateLabel = getDateLabel(messageDate);
      const showDate = !lastDate || getDateLabel(lastDate) !== dateLabel;
      const showSender = message.senderId !== lastSenderId || showDate;

      if (showDate && dateLabel !== currentDateLabel) {
        if (groups.length > 0 || index > 0) {
          groups.push({ dateLabel, messages: [] });
        }
        currentDateLabel = dateLabel;
      }

      if (groups.length === 0) {
        groups.push({ dateLabel, messages: [] });
      }

      groups[groups.length - 1].messages.push({
        message,
        showSender,
        showDate: false, // Don't show date on message itself, use separator
      });

      lastSenderId = message.senderId;
      lastDate = messageDate;
    });

    return groups;
  }, [messages]);

  // Memoize rendered messages
  const renderedMessages = useMemo(() => {
    if (messages.length === 0) {
      return (
        <EmptyMessageContent $isOwn={false} $color="#6b7280">
          No messages yet. Start the conversation!
        </EmptyMessageContent>
      );
    }

    const result: React.ReactNode[] = [];

    groupedMessages.forEach((group, groupIndex) => {
      // Add date separator
      if (group.dateLabel && (groupIndex > 0 || group.messages.length > 0)) {
        result.push(
          <DateSeparator key={`date-${group.dateLabel}-${groupIndex}`}>
            <span>{group.dateLabel}</span>
          </DateSeparator>,
        );
      }

      // Render messages in group
      group.messages.forEach(({ message, showSender }) => {
        const isOwn = isCurrentUser(message.senderId);
        const senderColor = getSenderColor(message.senderId);
        const tokenUsage = calculateTokenUsage(message.tokenUsage);
        const isAssistant = message.senderId === 'assistant';
        const isNewMessage = newMessageIds.has(message.id);
        const isFailed = failedMessageIds.has(message.id);

        // Assistant: render message.content as markdown so we show HTML, not raw string.
        // During streaming, message.content grows (typewriter); we re-render on every update so markdown is transformed as text streams in.
        let content: React.ReactNode;
        if (isAssistant) {
          if (!message.content?.trim()) {
            content = <TypingIndicator showText={false} compact />;
          } else if (searchQuery) {
            const highlightedContent = injectCitationLinksHtml(
              highlightSearchTerm(message.content, searchQuery),
              getCitationColor,
            );
            content = (
              <AssistantContentWrapper onClick={handleCitationLinkClick(message)}>
                <HighlightedContent
                  dangerouslySetInnerHTML={{ __html: highlightedContent }}
                />
              </AssistantContentWrapper>
            );
          } else {
            const contentWithPlaceholders = injectCitationPlaceholders(
              message.content,
              message.id,
            );
            content = (
              <AssistantContentWrapper onClick={handleCitationLinkClick(message)}>
                <MarkdownRenderer
                  content={contentWithPlaceholders}
                  citationMessageId={message.id}
                  getCitationColor={getCitationColor}
                />
              </AssistantContentWrapper>
            );
          }
        } else {
          let textWithLinks = makeLinksClickable(message.content);
          if (searchQuery) {
            textWithLinks = highlightSearchTerm(textWithLinks, searchQuery);
          }
          content = (
            <TextWithLinks
              dangerouslySetInnerHTML={{ __html: textWithLinks }}
            />
          );
        }

        const isHighlighted = highlightedMessageId === message.id;
        const isSelected = selectedMessageIds.has(message.id);

        result.push(
          <MessageGroup key={message.id} $showSender={showSender}>
            {showSender && !isOwn && (
              <MessageSender style={{ color: senderColor }}>
                {message.senderName}
              </MessageSender>
            )}
            <MessageBubbleWrapper
              id={`message-${message.id}`}
              $isOwn={!isOwn}
              $isNew={isNewMessage}
              $isSelected={isSelected}
              $isHighlighted={isHighlighted}
              ref={(el) => {
                if (isHighlighted && el) {
                  setTimeout(() => {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    el.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
                    setTimeout(() => {
                      el.style.backgroundColor = '';
                    }, 2000);
                  }, 100);
                }
              }}
              onClick={(e) => {
                // Allow multi-select with Ctrl/Cmd
                if (e.ctrlKey || e.metaKey) {
                  e.preventDefault();
                  toggleMessageSelection(message.id);
                } else if (onMessageClick) {
                  onMessageClick(message.id);
                }
              }}
              onMouseEnter={(e) => {
                const actions = e.currentTarget.querySelector(
                  '.message-actions',
                ) as HTMLElement;
                if (actions) actions.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                const actions = e.currentTarget.querySelector(
                  '.message-actions',
                ) as HTMLElement;
                if (actions) actions.style.opacity = '0';
              }}
            >
              {isFailed ? (
                <FailedMessageContent $isOwn={!isOwn} $color={senderColor}>
                  {content}
                </FailedMessageContent>
              ) : (
                <MessageContent $isOwn={!isOwn} $color={senderColor}>
                  {content}
                  {message.citations && message.citations.length > 0 && (
                    <Spacer />
                  )}
                  {isAssistant &&
                    message.citations &&
                    message.citations.length > 0 && (
                      <>
                        <CitationsAccordionHeader
                          type="button"
                          onClick={() => {
                            setExpandedCitationMessageIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(message.id)) next.delete(message.id);
                              else next.add(message.id);
                              return next;
                            });
                          }}
                          aria-expanded={expandedCitationMessageIds.has(
                            message.id,
                          )}
                        >
                          Citations used
                          <span aria-hidden>
                            {expandedCitationMessageIds.has(message.id)
                              ? ' ▼'
                              : ' ▶'}
                          </span>
                        </CitationsAccordionHeader>
                        {expandedCitationMessageIds.has(message.id) && (
                          <CitationsSection>
                            <CitationContainer>
                              {message.citations.map((citation) => {
                                const color = getCitationColor(
                                  citation.citationRef,
                                );
                                const snippet = citation.snippet?.trim() || '';
                                return (
                                  <CitationItem
                                    key={citation.citationRef}
                                    id={`citation-${message.id}-${citation.citationRef}`}
                                    $color={color}
                                    $clickable={!!onCitationClick}
                                    title={`${citation.sourceName}, page ${citation.pageNumber}${snippet ? ` — ${snippet}` : ''}`}
                                    onClick={
                                      onCitationClick
                                        ? (e) => {
                                            e.stopPropagation();
                                            onCitationClick(
                                              citation,
                                              message.id,
                                            );
                                          }
                                        : undefined
                                    }
                                    onKeyDown={
                                      onCitationClick
                                        ? (e) => {
                                            if (
                                              e.key === 'Enter' ||
                                              e.key === ' '
                                            ) {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              onCitationClick(
                                                citation,
                                                message.id,
                                              );
                                            }
                                          }
                                        : undefined
                                    }
                                    role={
                                      onCitationClick ? 'button' : undefined
                                    }
                                    tabIndex={onCitationClick ? 0 : undefined}
                                  >
                                    <CitationNumber $color={color}>
                                      [{citation.citationRef}]
                                    </CitationNumber>
                                    <CitationSnippet
                                      title={snippet || undefined}
                                    >
                                      {snippet || '—'}
                                    </CitationSnippet>
                                  </CitationItem>
                                );
                              })}
                            </CitationContainer>
                          </CitationsSection>
                        )}
                      </>
                    )}
                </MessageContent>
              )}
              <MessageActionsContainer
                $isOwn={isOwn}
                className="message-actions"
              >
                <MessageActionButton
                  $isOwn={isOwn}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyMessage(message.content);
                  }}
                  title="Copy message"
                >
                  📋
                </MessageActionButton>
                {onPinMessage && (
                  <MessageActionButton
                    $isOwn={isOwn}
                    $isPinned={pinnedMessageIds.has(message.id)}
                    onClick={(e) => {
                      e.stopPropagation();
                      onPinMessage(message.id);
                    }}
                    title={
                      pinnedMessageIds.has(message.id)
                        ? 'Unpin message'
                        : 'Pin message'
                    }
                  >
                    {pinnedMessageIds.has(message.id) ? '📌' : '📍'}
                  </MessageActionButton>
                )}
              </MessageActionsContainer>
              <MessageTimeWrapper
                $isOwn={!isOwn}
                title={formatFullTime(message.timestamp)}
              >
                {formatRelativeTimeMemo(message.timestamp)}
                {isAssistant && tokenUsage !== null && (
                  <TokenUsageSpan>
                    • {tokenUsage.toLocaleString()} tokens
                  </TokenUsageSpan>
                )}
              </MessageTimeWrapper>
              {isFailed && onRetryMessage && (
                <RetryButton onClick={() => onRetryMessage(message.id)}>
                  Retry
                </RetryButton>
              )}
            </MessageBubbleWrapper>
          </MessageGroup>,
        );
      });
    });

    return result;
  }, [messages.length, groupedMessages, isCurrentUser, getSenderColor, calculateTokenUsage, newMessageIds, failedMessageIds, highlightedMessageId, selectedMessageIds, expandedCitationMessageIds, onPinMessage, pinnedMessageIds, formatFullTime, formatRelativeTimeMemo, onRetryMessage, searchQuery, handleCitationLinkClick, onMessageClick, toggleMessageSelection, onCitationClick, handleCopyMessage]);

  return (
    <ChatContainer>
      {(selectedText || selectedMessageIds.size > 0) && (
        <SelectionBar>
          <span>
            {selectedText
              ? `${selectedText.length} characters selected`
              : `${selectedMessageIds.size} message(s) selected`}
          </span>
          <SelectionActions>
            {ENABLE_SELECTED_TEXT_COPY && selectedText && (
              <SelectionButton onClick={handleCopySelectedText}>
                Copy Text
              </SelectionButton>
            )}
            {selectedMessageIds.size > 0 && (
              <SelectionButton onClick={handleCopySelectedMessages}>
                Copy Messages
              </SelectionButton>
            )}
            <SelectionButton
              onClick={() => {
                setSelectedText('');
                setSelectedMessageIds(new Set());
                window.getSelection()?.removeAllRanges();
              }}
            >
              Clear
            </SelectionButton>
          </SelectionActions>
        </SelectionBar>
      )}
      <MessagesContainer
        data-tour="ask-session-chat"
        ref={messagesContainerRef}
      >
        {renderedMessages}
        {isTyping && (
          <TypingIndicator
            userName={typingUserName}
            elapsedTime={elapsedTime}
            statusMessage={processingStatus}
          />
        )}
        <div ref={messagesEndRef} />
        {showScrollButton && (
          <ScrollButtonContainer>
            <ScrollButton onClick={scrollToBottom} title="Scroll to bottom">
              ↓
            </ScrollButton>
          </ScrollButtonContainer>
        )}
      </MessagesContainer>
    </ChatContainer>
  );
};
